import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { reportApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExplainButton, ExplainPanel } from "@/components/shared/ExplainPanel";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import {
  Download, Share2, FileText, Loader2, ShieldCheck, AlertTriangle,
  Gem, Lock, Landmark, CheckCircle2, TrendingUp
} from "lucide-react";

const OWNERSHIP_COLORS = { owned: "#FFD700", custody: "#3B82F6", restricted: "#6366F1" };

export default function ReportPage() {
  const { formatMoney } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [explainData, setExplainData] = useState(null);
  const [explainOpen, setExplainOpen] = useState(false);

  useEffect(() => {
    reportApi.data().then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await reportApi.downloadPdf();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `sentient-vault-report-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    setDownloading(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>;
  if (!data) return <div className="text-center text-slate-500 py-16">Unable to load report data.</div>;

  const o = data.ownership;
  const pieData = [
    { name: "Owned", value: o.owned, color: OWNERSHIP_COLORS.owned },
    { name: "Custody", value: o.custody, color: OWNERSHIP_COLORS.custody },
    { name: "Restricted", value: o.restricted, color: OWNERSHIP_COLORS.restricted },
  ].filter(d => d.value > 0);

  const vaultData = data.vaults.map(v => ({ name: v.label.replace(" Custody", "").replace(" Obligation", ""), balance: v.balance }));

  return (
    <div className="space-y-6" data-testid="report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Financial Truth Report</h1>
          <p className="text-sm text-slate-500 mt-0.5">{data.org_name} — Generated {new Date(data.generated_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-700 gap-2 text-sm" onClick={() => navigator.clipboard.writeText(window.location.href)} data-testid="share-report-btn">
            <Share2 className="w-4 h-4" /> Share
          </Button>
          <Button className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 gap-2 text-sm" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn">
            <Download className="w-4 h-4" /> {downloading ? "Generating..." : "Download PDF"}
          </Button>
        </div>
      </div>

      {/* 1. Ownership Summary */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">1. Ownership Summary</CardTitle>
            <ExplainButton size="md" onClick={() => { setExplainData({ title: "Ownership Summary", what: "Shows who truly owns the money in your accounts. Not all cash in your bank is yours — much of it belongs to tax authorities, suppliers, and customers.", affects: ["Investment decisions", "Dividend capacity", "Borrowing needs"] }); setExplainOpen(true); }} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex justify-center">
              <ResponsiveContainer width={220} height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" stroke="none">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatMoney(v)} contentStyle={{ background: "rgba(10,16,32,0.97)", border: "1px solid rgba(120,180,255,0.25)", borderRadius: "12px", padding: "10px 14px", color: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-3">
              <div className="text-center md:text-left mb-2">
                <span className="text-xs text-slate-500">Total Tracked Value</span>
                <div className="text-3xl font-mono font-bold text-slate-50 tabular">{formatMoney(o.total)}</div>
              </div>
              {[
                { label: "Owned Capital", value: o.owned, color: "#FFD700", icon: Gem, desc: "Truly yours" },
                { label: "Custody Funds", value: o.custody, color: "#3B82F6", icon: ShieldCheck, desc: "Belongs to others" },
                { label: "Restricted Capital", value: o.restricted, color: "#6366F1", icon: Lock, desc: "Usable, not owned" },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between p-2 rounded-lg" style={{ borderLeft: `3px solid ${item.color}` }}>
                  <div className="flex items-center gap-2">
                    <item.icon className="w-4 h-4" style={{ color: item.color }} />
                    <div>
                      <span className="text-xs text-slate-300">{item.label}</span>
                      <span className="text-[10px] text-slate-600 block">{item.desc}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-mono tabular text-slate-100">{formatMoney(item.value)}</span>
                    <span className="text-[10px] text-slate-600 block">{o.total > 0 ? Math.round(item.value / o.total * 100) : 0}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2. Vault Breakdown */}
      <Card className="bg-slate-900/40 border-slate-800/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">2. Vault Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={vaultData} layout="vertical">
              <XAxis type="number" tick={{ fill: "#475569", fontSize: 10 }} axisLine={false} tickFormatter={v => `£${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} width={100} />
              <Tooltip formatter={v => formatMoney(v)} contentStyle={{ background: "rgba(10,16,32,0.97)", border: "1px solid rgba(120,180,255,0.25)", borderRadius: "12px", padding: "10px 14px", color: "#fff" }} />
              <Bar dataKey="balance" radius={[0, 4, 4, 0]}>
                {vaultData.map((_, i) => <Cell key={i} fill={["#10B981","#F59E0B","#3B82F6","#8B5CF6","#6366F1","#FFD700"][i % 6]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* 3. Obligations + 4. Profit side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">3. Obligations</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-mono font-bold text-slate-50 mb-3">{formatMoney(data.obligations.total)}</div>
            <div className="space-y-1.5">
              {data.obligations.items?.slice(0, 5).map((o, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[8px] ${o.priority === "CRITICAL" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"} border-none`}>{o.priority}</Badge>
                    <span className="text-slate-400">{o.claimant}</span>
                  </div>
                  <span className="font-mono text-slate-300">{formatMoney(o.amount)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">4. True Profit Analysis</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[
                { label: "Gross Revenue", value: data.profit.gross_revenue, color: "text-emerald-400" },
                { label: "Total Expenses", value: -data.profit.expenses, color: "text-red-400" },
                { label: "Adjustments", value: -data.profit.adjustments, color: "text-amber-400" },
                { label: "Profit Candidate", value: data.profit.profit_candidate, color: "text-cyan-400" },
              ].map(item => (
                <div key={item.label} className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{item.label}</span>
                  <span className={`text-sm font-mono tabular ${item.color}`}>{formatMoney(Math.abs(item.value))}</span>
                </div>
              ))}
              <div className="border-t border-slate-800 pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-300 font-semibold">True Available Profit</span>
                  <span className="text-lg font-mono tabular text-amber-400 font-bold">{formatMoney(Math.max(0, o.owned - data.obligations.total))}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 5. Behaviour + 6. Risks side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">5. Behaviour Score</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 mb-3">
              <Badge className={`text-sm font-mono px-3 py-1 ${data.behaviour.state === "STRATEGIC" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : data.behaviour.state === "EMOTIONAL" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"} border`}>
                {data.behaviour.state}
              </Badge>
            </div>
            <div className="space-y-1.5">
              {Object.entries(data.behaviour.metrics || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{k.replace(/_/g, " ").replace(/INDEX|RATIO/g, "").trim()}</span>
                  <span className={`font-mono ${v >= 70 ? "text-emerald-400" : v >= 40 ? "text-amber-400" : "text-red-400"}`}>{v.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">6. Risk Summary</CardTitle></CardHeader>
          <CardContent>
            {data.risks.length === 0 ? (
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">No significant risks detected</span>
              </div>
            ) : (
              <div className="space-y-2">
                {data.risks.map((r, i) => (
                  <div key={i} className={`p-2 rounded-lg border ${r.severity === "CRITICAL" ? "border-red-500/20 bg-red-500/5" : "border-amber-500/20 bg-amber-500/5"}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={`w-3.5 h-3.5 ${r.severity === "CRITICAL" ? "text-red-400" : "text-amber-400"}`} />
                      <span className="text-xs text-slate-300">{r.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 7. AI Commentary */}
      <Card className="bg-gradient-to-br from-slate-900/60 to-slate-900/30 border-cyan-500/15">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-cyan-400 font-mono uppercase tracking-wider">7. AI Commentary</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-slate-300 leading-relaxed">
            Of the total tracked value of {formatMoney(o.total)}, {formatMoney(o.owned)} ({o.total > 0 ? Math.round(o.owned / o.total * 100) : 0}%) is truly owned by the business.
            {" "}{formatMoney(o.custody)} is held in custody for tax authorities, suppliers, and customers.
            {" "}{formatMoney(o.restricted)} is restricted loan capital.
            {" "}The business has {data.obligations.count} active obligations totalling {formatMoney(data.obligations.total)}.
            {" "}Behavioural state: {data.behaviour.state}. Runway: approximately {data.snapshot?.runway_days || "N/A"} days.
            {data.risks.length === 0 ? " No significant risks detected." : ` ${data.risks.length} active risk(s) require attention.`}
          </p>
        </CardContent>
      </Card>

      <ExplainPanel open={explainOpen} onClose={() => setExplainOpen(false)} data={explainData} />
    </div>
  );
}
